import { useI18n, type MessageKey } from '@ecom/i18n';
import { delegationsOf, isDelegationOf } from '@ecom/i18n/delegations';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { GOVERNORATES } from '@ecom/i18n/governorates';
import { validateTunisianPhone } from '@ecom/i18n/phone';
import { Button, Card, Checkbox, Field, Input, RadioGroup, Select } from '@ecom/ui-kit';
import {
  type ReactElement,
  type SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateManualOrderMutation } from '../api';
import { MANUAL_CHANNELS, type ManualChannel, sourceLabel } from '../attribution';
import type { CreateManualOrderBody, ManualPaymentMethod } from '../types';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { useCustomersQuery } from '@/features/clients/api';
import { useProductsQuery, useVariantsQuery } from '@/features/products/api';
import { formatCatalogPrice } from '@/features/products/format-price';
import type { ProductWithStock } from '@/features/products/types';
import { useMyStoreQuery } from '@/features/store/api';

export type ManualEntryKind = 'order' | 'sale';

interface Line {
  readonly key: string;
  productId: string;
  variantId: string;
  quantity: string;
}

const PAYMENT_LABEL_KEY: Record<ManualPaymentMethod, MessageKey> = {
  cod: 'merchant.manual.payment.cod',
  cash: 'merchant.manual.payment.cash',
  bank_transfer: 'merchant.manual.payment.bankTransfer',
  card: 'merchant.manual.payment.card',
  cheque: 'merchant.manual.payment.cheque',
  other: 'merchant.manual.payment.other',
};

const ORDER_METHODS: readonly ManualPaymentMethod[] = [
  'cod',
  'bank_transfer',
  'cash',
  'card',
  'cheque',
  'other',
];
const SALE_METHODS: readonly ManualPaymentMethod[] = [
  'cash',
  'card',
  'bank_transfer',
  'cheque',
  'other',
];

const PICKUP_DELEGATION = 'Retrait en boutique';
const PICKUP_ADDRESS = 'Remise en main propre en boutique';

let lineSeq = 0;
const newLine = (): Line => ({
  key: `line-${String((lineSeq += 1))}`,
  productId: '',
  variantId: '',
  quantity: '1',
});

/** `datetime-local` value for "now" in the browser's own time zone. */
function localNow(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

const freshKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${String(Date.now())}-${String(Math.random()).slice(2)}`;

/**
 * « Ajouter une commande » / « Ajouter une vente » (migration 0096, `POST /merchant/orders/manual`).
 *
 * The prices and totals shown here are an ESTIMATE from the catalogue already loaded: the API recomputes every amount
 * (catalogue price, shop delivery fee, coupon) and checks the stock, so what is saved never depends on these numbers.
 * One idempotency key per submission, kept across a retry of the SAME submission, so a double click cannot create two
 * orders; a new key is drawn only after a success.
 */
export function ManualEntryForm({ kind }: { kind: ManualEntryKind }): ReactElement {
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const products = useProductsQuery();
  const customers = useCustomersQuery();
  const store = useMyStoreQuery();
  const create = useCreateManualOrderMutation();
  const idempotencyKey = useRef(freshKey());

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [lines, setLines] = useState<Line[]>([newLine()]);
  const [couponCode, setCouponCode] = useState('');
  const [pickup, setPickup] = useState(kind === 'sale');
  const [governorate, setGovernorate] = useState('');
  const [delegation, setDelegation] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [applyDeliveryFee, setApplyDeliveryFee] = useState(kind === 'order');
  const [status, setStatus] = useState<'PAYMENT_CONFIRMED' | 'PROCESSING'>('PAYMENT_CONFIRMED');
  const [paymentMethod, setPaymentMethod] = useState<ManualPaymentMethod>(
    kind === 'sale' ? 'cash' : 'cod',
  );
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid'>(
    kind === 'sale' ? 'paid' : 'pending',
  );
  const [paymentReference, setPaymentReference] = useState('');
  const [soldAt, setSoldAt] = useState(localNow);
  const [internalNote, setInternalNote] = useState('');
  const [channel, setChannel] = useState<ManualChannel | ''>('');
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const activeProducts = useMemo(
    () => (products.data ?? []).filter((product) => product.status === 'active'),
    [products.data],
  );
  const productById = useMemo(
    () => new Map(activeProducts.map((p) => [p.id, p])),
    [activeProducts],
  );
  const [linePrices, setLinePrices] = useState<Record<string, number | null>>({});

  const subtotal = lines.reduce((sum, line) => {
    const price = linePrices[line.key];
    const quantity = Number(line.quantity);
    return price === undefined || price === null || !Number.isInteger(quantity)
      ? sum
      : sum + price * quantity;
  }, 0);
  const deliveryFee =
    applyDeliveryFee && !(kind === 'sale' && pickup) ? (store.data?.deliveryFee ?? 0) : 0;

  const governorateOptions = [
    ...GOVERNORATES.map((g) => ({ value: g.code, label: locale === 'ar' ? g.nameAr : g.nameFr })),
  ];
  // Only the chosen governorate's delegations, so the parcel address is never misspelt or mismatched.
  const delegationOptions = delegationsOf(governorate).map((name) => ({
    value: name,
    label: name,
  }));

  function validate(): Partial<Record<string, string>> {
    const next: Partial<Record<string, string>> = {};
    if (customerName.trim() === '') next.customerName = t('merchant.manual.errors.nameRequired');
    if (!validateTunisianPhone(customerPhone).valid)
      next.customerPhone = t('merchant.manual.errors.phoneInvalid');
    if (customerEmail.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(customerEmail.trim())) {
      next.customerEmail = t('merchant.manual.errors.emailInvalid');
    }
    const seen = new Set<string>();
    lines.forEach((line) => {
      const quantity = Number(line.quantity);
      if (line.productId === '')
        next[`line-${line.key}`] = t('merchant.manual.errors.productRequired');
      else if (linePrices[line.key] === null)
        next[`line-${line.key}`] = t('merchant.manual.errors.variantRequired');
      else if (!Number.isInteger(quantity) || quantity < 1 || quantity > 999) {
        next[`line-${line.key}`] = t('merchant.manual.errors.quantityInvalid');
      }
      const identity = `${line.productId}:${line.variantId}`;
      if (line.productId !== '' && seen.has(identity))
        next[`line-${line.key}`] = t('merchant.manual.errors.duplicateLine');
      seen.add(identity);
    });
    if (governorate === '') next.governorate = t('merchant.manual.errors.governorateRequired');
    if (!(kind === 'sale' && pickup)) {
      if (delegation === '') next.delegation = t('merchant.manual.errors.delegationRequired');
      else if (!isDelegationOf(governorate, delegation))
        next.delegation = t('merchant.manual.errors.delegationMismatch');
      if (addressLine.trim() === '') next.addressLine = t('merchant.manual.errors.addressRequired');
    }
    if (
      kind === 'sale' &&
      paymentStatus === 'paid' &&
      paymentMethod !== 'cash' &&
      paymentReference.trim() === ''
    ) {
      next.paymentReference = t('merchant.manual.errors.referenceRequired');
    }
    if (kind === 'sale') {
      const date = new Date(soldAt);
      const now = Date.now();
      if (
        Number.isNaN(date.getTime()) ||
        date.getTime() > now + 5 * 60_000 ||
        date.getTime() < now - 30 * 86_400_000
      ) {
        next.soldAt = t('merchant.manual.errors.saleDate');
      }
    }
    return next;
  }

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error(t('merchant.manual.errors.fixForm'));
      return;
    }
    const isPickup = kind === 'sale' && pickup;
    const body: CreateManualOrderBody = {
      kind,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      customerEmail: customerEmail.trim() === '' ? null : customerEmail.trim(),
      items: lines.map((line) => ({
        productId: line.productId,
        variantId: line.variantId === '' ? null : line.variantId,
        quantity: Number(line.quantity),
      })),
      couponCode: couponCode.trim() === '' ? null : couponCode.trim(),
      deliveryGovernorate: governorate,
      deliveryDelegation: isPickup ? PICKUP_DELEGATION : delegation,
      deliveryAddressLine: isPickup ? PICKUP_ADDRESS : addressLine.trim(),
      deliveryAdditionalInfo:
        isPickup || additionalInfo.trim() === '' ? null : additionalInfo.trim(),
      applyDeliveryFee: isPickup ? false : applyDeliveryFee,
      ...(kind === 'order' ? { status } : { soldAt: new Date(soldAt).toISOString() }),
      paymentMethod,
      paymentStatus: kind === 'order' ? 'pending' : paymentStatus,
      paymentReference: paymentReference.trim() === '' ? null : paymentReference.trim(),
      internalNote: internalNote.trim() === '' ? null : internalNote.trim(),
      channel: channel === '' ? null : channel,
    };
    try {
      const result = await create.mutateAsync({ body, idempotencyKey: idempotencyKey.current });
      idempotencyKey.current = freshKey();
      toast.success(
        t(kind === 'sale' ? 'merchant.manual.saleCreated' : 'merchant.manual.orderCreated', {
          number: result.orderNumber,
          total: formatCatalogPrice(result.grandTotal),
        }),
      );
      void navigate(kind === 'sale' ? '/sales' : '/orders');
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  const methods = kind === 'sale' ? SALE_METHODS : ORDER_METHODS;
  const customerOptions = [
    ...customers.customers.map((c) => ({ value: c.phone, label: `${c.name} · ${c.phone}` })),
  ];

  return (
    <form
      noValidate
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      className="flex flex-col gap-4"
    >
      <Card className="flex flex-col gap-4 p-5">
        <h2 className="font-display text-base font-semibold text-ink">
          {t('merchant.manual.customer')}
        </h2>
        {customers.customers.length > 0 ? (
          <Field label={t('merchant.manual.existingCustomer')} optional>
            {(bind) => (
              <Select
                {...bind}
                options={customerOptions}
                placeholder={t('merchant.manual.newCustomer')}
                value=""
                onChange={(event) => {
                  const chosen = customers.customers.find((c) => c.phone === event.target.value);
                  if (chosen !== undefined) {
                    setCustomerName(chosen.name);
                    setCustomerPhone(chosen.phone);
                  }
                }}
              />
            )}
          </Field>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label={t('merchant.manual.customerName')}
            {...(errors.customerName !== undefined ? { error: errors.customerName } : {})}
          >
            {(bind) => (
              <Input
                {...bind}
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                }}
                autoComplete="off"
              />
            )}
          </Field>
          <Field
            label={t('merchant.manual.customerPhone')}
            hint={t('merchant.manual.customerPhoneHint')}
            {...(errors.customerPhone !== undefined ? { error: errors.customerPhone } : {})}
          >
            {(bind) => (
              <Input
                {...bind}
                value={customerPhone}
                onChange={(e) => {
                  setCustomerPhone(e.target.value);
                }}
                type="tel"
                inputMode="tel"
                dir="ltr"
                autoComplete="off"
              />
            )}
          </Field>
          <Field
            label={t('merchant.manual.customerEmail')}
            optional
            {...(errors.customerEmail !== undefined ? { error: errors.customerEmail } : {})}
          >
            {(bind) => (
              <Input
                {...bind}
                value={customerEmail}
                onChange={(e) => {
                  setCustomerEmail(e.target.value);
                }}
                type="email"
                dir="ltr"
                autoComplete="off"
              />
            )}
          </Field>
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="font-display text-base font-semibold text-ink">
          {t('merchant.manual.products')}
        </h2>
        {products.isPending ? (
          <p className="text-sm text-inkSoft">{t('merchant.manual.loadingProducts')}</p>
        ) : activeProducts.length === 0 ? (
          <p className="text-sm text-warn">{t('merchant.manual.noActiveProducts')}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {lines.map((line) => (
              <LineRow
                key={line.key}
                line={line}
                products={activeProducts}
                product={productById.get(line.productId)}
                error={errors[`line-${line.key}`]}
                canRemove={lines.length > 1}
                onChange={(patch) => {
                  setLines((current) =>
                    current.map((l) => (l.key === line.key ? { ...l, ...patch } : l)),
                  );
                }}
                onPrice={(price) => {
                  setLinePrices((current) =>
                    current[line.key] === price ? current : { ...current, [line.key]: price },
                  );
                }}
                onRemove={() => {
                  setLines((current) => current.filter((l) => l.key !== line.key));
                }}
              />
            ))}
          </ul>
        )}
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={lines.length >= 50 || activeProducts.length === 0}
            onClick={() => {
              setLines((current) => [...current, newLine()]);
            }}
          >
            {t('merchant.manual.addLine')}
          </Button>
        </div>
        <Field label={t('merchant.manual.coupon')} hint={t('merchant.manual.couponHint')} optional>
          {(bind) => (
            <Input
              {...bind}
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
              }}
              dir="ltr"
              autoComplete="off"
            />
          )}
        </Field>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="font-display text-base font-semibold text-ink">
          {t(kind === 'sale' ? 'merchant.manual.handover' : 'merchant.manual.delivery')}
        </h2>
        {kind === 'sale' ? (
          <Checkbox label={t('merchant.manual.pickup')} checked={pickup} onChange={setPickup} />
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t(
              kind === 'sale' && pickup
                ? 'merchant.manual.shopGovernorate'
                : 'merchant.manual.governorate',
            )}
            {...(errors.governorate !== undefined ? { error: errors.governorate } : {})}
          >
            {(bind) => (
              <Select
                {...bind}
                options={governorateOptions}
                placeholder={t('merchant.manual.chooseGovernorate')}
                value={governorate}
                onChange={(e) => {
                  setGovernorate(e.target.value);
                  setDelegation('');
                }}
              />
            )}
          </Field>
          {!(kind === 'sale' && pickup) ? (
            <>
              <Field
                label={t('merchant.manual.delegation')}
                {...(errors.delegation !== undefined ? { error: errors.delegation } : {})}
              >
                {(bind) => (
                  <Select
                    {...bind}
                    options={delegationOptions}
                    placeholder={t(
                      governorate === ''
                        ? 'merchant.manual.chooseGovernorateFirst'
                        : 'merchant.manual.chooseGovernorate',
                    )}
                    disabled={governorate === ''}
                    value={delegation}
                    onChange={(e) => {
                      setDelegation(e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field
                label={t('merchant.manual.address')}
                {...(errors.addressLine !== undefined ? { error: errors.addressLine } : {})}
              >
                {(bind) => (
                  <Input
                    {...bind}
                    value={addressLine}
                    onChange={(e) => {
                      setAddressLine(e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label={t('merchant.manual.additionalInfo')} optional>
                {(bind) => (
                  <Input
                    {...bind}
                    value={additionalInfo}
                    onChange={(e) => {
                      setAdditionalInfo(e.target.value);
                    }}
                  />
                )}
              </Field>
            </>
          ) : null}
        </div>
        {!(kind === 'sale' && pickup) ? (
          <Checkbox
            label={t('merchant.manual.applyDeliveryFee', {
              fee: formatCatalogPrice(store.data?.deliveryFee ?? 0),
            })}
            checked={applyDeliveryFee}
            onChange={setApplyDeliveryFee}
          />
        ) : null}
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="font-display text-base font-semibold text-ink">
          {t('merchant.manual.paymentAndStatus')}
        </h2>
        {kind === 'order' ? (
          <RadioGroup
            legend={t('merchant.manual.status')}
            name="manual-status"
            value={status}
            onChange={(value) => {
              setStatus(value === 'PROCESSING' ? 'PROCESSING' : 'PAYMENT_CONFIRMED');
            }}
            options={[
              {
                value: 'PAYMENT_CONFIRMED',
                label: t('merchant.manual.status.toProcess'),
                description: t('merchant.manual.status.toProcessHint'),
              },
              {
                value: 'PROCESSING',
                label: t('merchant.manual.status.processing'),
                description: t('merchant.manual.status.processingHint'),
              },
            ]}
          />
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('merchant.manual.paymentMethod')}>
            {(bind) => (
              <Select
                {...bind}
                required
                value={paymentMethod}
                options={methods.map((m) => ({ value: m, label: t(PAYMENT_LABEL_KEY[m]) }))}
                onChange={(e) => {
                  setPaymentMethod(e.target.value as ManualPaymentMethod);
                }}
              />
            )}
          </Field>
          {kind === 'sale' ? (
            <Field label={t('merchant.manual.paymentStatus')}>
              {(bind) => (
                <Select
                  {...bind}
                  required
                  value={paymentStatus}
                  options={[
                    { value: 'paid', label: t('merchant.manual.paymentPaid') },
                    { value: 'pending', label: t('merchant.manual.paymentPending') },
                  ]}
                  onChange={(e) => {
                    setPaymentStatus(e.target.value === 'pending' ? 'pending' : 'paid');
                  }}
                />
              )}
            </Field>
          ) : (
            <p className="self-end rounded-md bg-ground px-3 py-2 text-sm text-inkSoft">
              {t('merchant.manual.orderPaymentPending')}
            </p>
          )}
          <Field
            label={t('merchant.manual.paymentReference')}
            hint={t('merchant.manual.paymentReferenceHint')}
            optional={!(kind === 'sale' && paymentStatus === 'paid' && paymentMethod !== 'cash')}
            {...(errors.paymentReference !== undefined ? { error: errors.paymentReference } : {})}
          >
            {(bind) => (
              <Input
                {...bind}
                value={paymentReference}
                onChange={(e) => {
                  setPaymentReference(e.target.value);
                }}
                dir="ltr"
                autoComplete="off"
              />
            )}
          </Field>
          {kind === 'sale' ? (
            <Field
              label={t('merchant.manual.saleDate')}
              hint={t('merchant.manual.saleDateHint')}
              {...(errors.soldAt !== undefined ? { error: errors.soldAt } : {})}
            >
              {(bind) => (
                <Input
                  {...bind}
                  type="datetime-local"
                  value={soldAt}
                  max={localNow()}
                  onChange={(e) => {
                    setSoldAt(e.target.value);
                  }}
                />
              )}
            </Field>
          ) : null}
        </div>
        <Field
          label={t('merchant.attribution.channel')}
          hint={t('merchant.attribution.channelHint')}
          optional
        >
          {(bind) => (
            <Select
              {...bind}
              value={channel}
              placeholder={t('merchant.attribution.channelNone')}
              options={MANUAL_CHANNELS.map((value) => ({ value, label: sourceLabel(value, t) }))}
              onChange={(e) => {
                setChannel(e.target.value as ManualChannel | '');
              }}
            />
          )}
        </Field>
        <Field
          label={t('merchant.manual.internalNote')}
          hint={t('merchant.manual.internalNoteHint')}
          optional
        >
          {(bind) => (
            <textarea
              {...bind}
              rows={2}
              maxLength={2000}
              value={internalNote}
              onChange={(e) => {
                setInternalNote(e.target.value);
              }}
              className="ecom-input"
            />
          )}
        </Field>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
          <dt className="text-inkSoft">{t('merchant.manual.subtotal')}</dt>
          <dd className="text-end num text-ink">{formatCatalogPrice(subtotal)}</dd>
          <dt className="text-inkSoft">{t('merchant.manual.deliveryFee')}</dt>
          <dd className="text-end num text-ink">{formatCatalogPrice(deliveryFee)}</dd>
          {couponCode.trim() !== '' ? (
            <>
              <dt className="text-inkSoft">{t('merchant.manual.discount')}</dt>
              <dd className="text-end text-inkSoft">{t('merchant.manual.discountOnSave')}</dd>
            </>
          ) : null}
          <dt className="font-semibold text-ink">{t('merchant.manual.estimatedTotal')}</dt>
          <dd className="text-end num font-semibold text-ink">
            {formatCatalogPrice(subtotal + deliveryFee)}
          </dd>
        </dl>
        <p className="text-xs text-inkSoft">{t('merchant.manual.serverRecomputes')}</p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              void navigate(kind === 'sale' ? '/sales' : '/orders');
            }}
          >
            {t('merchant.common.cancel')}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending
              ? t('merchant.manual.saving')
              : t(kind === 'sale' ? 'merchant.manual.saveSale' : 'merchant.manual.saveOrder')}
          </Button>
        </div>
      </Card>
    </form>
  );
}

function LineRow({
  line,
  products,
  product,
  error,
  canRemove,
  onChange,
  onPrice,
  onRemove,
}: {
  line: Line;
  products: readonly ProductWithStock[];
  product: ProductWithStock | undefined;
  error: string | undefined;
  canRemove: boolean;
  onChange: (patch: Partial<Line>) => void;
  onPrice: (price: number | null) => void;
  onRemove: () => void;
}): ReactElement {
  const { t } = useI18n();
  const variants = useVariantsQuery(product?.id);
  const activeVariants = (variants.data ?? []).filter((v) => v.isActive);
  const hasVariants = activeVariants.length > 0;
  const variant = activeVariants.find((v) => v.id === line.variantId);

  // The unit price this line will most likely be charged — the API re-reads it at save time.
  const price =
    product === undefined
      ? null
      : hasVariants
        ? variant === undefined
          ? null
          : (variant.priceOverride ?? product.price)
        : product.price;
  useEffect(() => {
    onPrice(price);
  }, [price, onPrice]);

  const available = hasVariants ? null : (product?.stock.quantityAvailable ?? null);
  const quantity = Number(line.quantity);

  return (
    <li className="flex flex-col gap-2 rounded-md border border-line p-3">
      <div className="grid gap-3 sm:grid-cols-[2fr_1.4fr_0.7fr_auto] sm:items-end">
        <Field label={t('merchant.manual.product')}>
          {(bind) => (
            <Select
              {...bind}
              value={line.productId}
              placeholder={t('merchant.manual.chooseProduct')}
              options={[
                ...products.map((p) => ({
                  value: p.id,
                  label: `${p.name} — ${formatCatalogPrice(p.price)}`,
                })),
              ]}
              onChange={(event) => {
                onChange({ productId: event.target.value, variantId: '' });
              }}
            />
          )}
        </Field>
        {hasVariants ? (
          <Field label={t('merchant.manual.variant')}>
            {(bind) => (
              <Select
                {...bind}
                value={line.variantId}
                placeholder={t('merchant.manual.chooseVariant')}
                options={[
                  ...activeVariants.map((v) => ({
                    value: v.id,
                    label: `${Object.values(v.attributes).map(String).join(' / ')} — ${formatCatalogPrice(v.priceOverride ?? product?.price ?? 0)}`,
                  })),
                ]}
                onChange={(event) => {
                  onChange({ variantId: event.target.value });
                }}
              />
            )}
          </Field>
        ) : (
          <div className="hidden sm:block" />
        )}
        <Field label={t('merchant.manual.quantity')}>
          {(bind) => (
            <Input
              {...bind}
              type="number"
              inputMode="numeric"
              min={1}
              max={999}
              step={1}
              value={line.quantity}
              onChange={(event) => {
                onChange({ quantity: event.target.value });
              }}
            />
          )}
        </Field>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!canRemove}
          onClick={onRemove}
        >
          {t('merchant.manual.removeLine')}
        </Button>
      </div>
      <div className="flex flex-wrap justify-between gap-2 text-xs text-inkSoft">
        <span>
          {available !== null
            ? t('merchant.manual.stockAvailable', { count: available })
            : product !== undefined && !hasVariants
              ? t('merchant.manual.stockUntracked')
              : ''}
          {available !== null && Number.isInteger(quantity) && quantity > available ? (
            <strong className="ms-2 text-critical">{t('merchant.manual.stockShort')}</strong>
          ) : null}
        </span>
        {price !== null && Number.isInteger(quantity) && quantity > 0 ? (
          <span className="num text-ink">{formatCatalogPrice(price * quantity)}</span>
        ) : null}
      </div>
      {error !== undefined ? (
        <p role="alert" className="text-sm text-critical">
          {error}
        </p>
      ) : null}
    </li>
  );
}
